<template>
  <div
    class="book-layout relative flex h-dvh overflow-hidden bg-[var(--color-bg)]"
    :class="{
      'items-center justify-center': isCover,
      'book-layout--zooming': isZooming,
    }"
  >
    <!-- 首頁封面：遠景書本 -->
    <div
      v-if="isCover"
      class="book-cover-wrap w-full h-dvh flex items-center justify-start py-4 overflow-visible"
      ref="coverWrap"
    >
      <!-- 書本 + 書籤群組（右移動畫作用在此） -->
      <div
        class="book-cover-group relative flex items-stretch w-full overflow-visible"
        style="height: calc(100vh - 2rem); filter: drop-shadow(0 8px 32px rgba(0,0,0,0.8)) drop-shadow(2px 4px 8px rgba(0,0,0,0.6));"
        ref="bookShell"
      >
        <!-- 左側置中補償（md 以上才顯示） -->
        <div class="book-cover-group__balancer flex-1 min-w-0 pointer-events-none hidden md:block"></div>

        <!-- 書本本體 -->
        <div
          class="book-shell relative flex overflow-visible shrink-0"
          style="height: calc(100vh - 2rem); width: auto; aspect-ratio: 3/4;"
          ref="bookBody"
        >
          <!-- 左書脊（768 以下隱藏） -->
          <div class="book-cover__spine relative z-[2] shrink-0 hidden xl:block" ref="bookSpine"></div>

          <!-- 翻頁容器 -->
          <div
            class="book-flip flex-1 relative"
            style="transform-origin: left center; transform-style: preserve-3d;"
            ref="bookFlip"
          >
            <!-- 正面 -->
            <div
              class="absolute inset-0 overflow-hidden bg-[var(--color-bg)] flex flex-col"
              style="backface-visibility: hidden; border-top: 1px solid rgba(255,255,255,0.06); border-bottom: 1px solid rgba(255,255,255,0.04);"
            >
              <slot />
              <div
                class="absolute inset-0 pointer-events-none opacity-0"
                style="background: linear-gradient(to left, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 40%, transparent 70%);"
                ref="flipShadow"
              ></div>
            </div>
            <!-- 背面 -->
            <div
              class="book-flip__back absolute inset-0 bg-[#1a1a1a]"
              style="backface-visibility: hidden; transform: rotateY(180deg); border-top: 1px solid rgba(255,255,255,0.06); border-bottom: 1px solid rgba(255,255,255,0.04);"
            ></div>
          </div>
        </div>

        <!-- 右側書籤列 -->
        <BookmarkNav @navigate="handleCoverNavigate" @menu="toggleMenu" />
      </div>
    </div>

    <!-- 內頁模式 -->
    <div
      v-else
      class="book-inner-wrap relative flex items-stretch w-full overflow-visible"
      style="height: calc(100vh - 2rem); margin: 1rem auto; filter: drop-shadow(0 8px 32px rgba(0,0,0,0.8)) drop-shadow(2px 4px 8px rgba(0,0,0,0.6));"
    >
      <!-- 左書籤欄（lg 以上顯示） -->
      <BookmarkNav class="book-left-nav hidden xl:flex" side="left" @navigate="handleNavigate" @menu="toggleMenu" />

      <!-- 主內容區 -->
      <main
        class="flex-1 min-w-0 relative bg-[var(--color-bg)] overflow-y-auto overflow-x-hidden"
        style="border-left: 1px solid rgba(255,255,255,0.05); border-right: 1px solid rgba(255,255,255,0.05); box-shadow: inset 10px 0 24px rgba(0,0,0,0.4), inset -10px 0 24px rgba(0,0,0,0.4); overscroll-behavior: none; -webkit-overflow-scrolling: touch;"
      >
        <slot />
        <PageFlip ref="pageFlip" />
      </main>

      <!-- 右書籤欄 -->
      <BookmarkNav side="right" @navigate="handleNavigate" @menu="toggleMenu" />
    </div>

    <!-- 手機漢堡（單一，覆蓋封面與內頁） -->
    <button
      class="mobile-menu-btn xl:hidden flex items-center fixed top-[1rem] right-0 z-[250] h-[3.2rem] border-none cursor-pointer"
      :class="{
        'mobile-menu-btn--open': menuOpen,
        'mobile-menu-btn--has-search': hasSearch,
      }"
      @click="toggleMenu"
      aria-label="開啟選單"
    >
      <span class="mobile-menu-btn__icon font-black tracking-[0.05em] text-[rgba(232,232,232,0.9)] whitespace-nowrap select-none">☰</span>
    </button>

    <!-- 手機版書籤 -->
    <BookmarkMobile
      ref="mobileMenu"
      :current-index="currentIndex"
      :pages="bookPages"
      @navigate="isCover ? handleCoverNavigate($event) : handleNavigate($event)"
      @close="menuOpen = false"
    />

    <!-- 關書蓋板 -->
    <div
      class="book-close-overlay hidden fixed inset-0 z-[300] bg-[var(--color-bg)] pointer-events-none"
      ref="closeOverlay"
    ></div>
  </div>
</template>

<script>
import { gsap } from 'gsap'
import { provide, ref } from 'vue'
import BookmarkNav from './BookmarkNav.vue'
import BookmarkMobile from './BookmarkMobile.vue'
import PageFlip from './PageFlip.vue'

export const BOOK_PAGES = [
  { name: 'Home',        route: '/',             label: 'Home' },
  { name: 'AboutMe',     route: '/about',        label: 'About Me' },
  { name: 'Skills',      route: '/skills',       label: 'Skills' },
  { name: 'Experiences', route: '/experiences',  label: 'Experiences' },
  { name: 'Items',       route: '/items',        label: 'Items' },
  { name: 'Blog',        route: '/blog',         label: 'Blog' },
  { name: 'Commission',  route: '/commission',   label: 'Commission' },
]

export default {
  name: 'BookLayout',
  components: { BookmarkNav, BookmarkMobile, PageFlip },

  setup() {
    const coverPreFlip = ref(null)
    provide('registerPreFlip', (fn) => { coverPreFlip.value = fn })
    return { coverPreFlip }
  },

  data() {
    return { isZooming: false, menuOpen: false }
  },

  computed: {
    bookPages() {
      const commissionOpen = this.$store.getters.commissionOpen
      return BOOK_PAGES.filter(p => p.name !== 'Commission' || commissionOpen)
    },
    innerPages() { return this.bookPages.slice(1) },
    currentIndex() {
      const route = this.$route.path
      if (route.startsWith('/items')) return BOOK_PAGES.findIndex(p => p.name === 'Items')
      if (route.startsWith('/blog'))  return BOOK_PAGES.findIndex(p => p.name === 'Blog')
      return BOOK_PAGES.findIndex(p => p.route === route)
    },
    isCover() { return this.currentIndex === 0 },
    hasSearch() {
      const p = this.$route.path
      return p.startsWith('/blog') || p.startsWith('/items')
    },
    prevPages() { return BOOK_PAGES.slice(1, this.currentIndex) },
    leftPages() { return [BOOK_PAGES[0], ...this.prevPages] },
    nextPages() { return BOOK_PAGES.slice(this.currentIndex + 1) },
  },

  watch: {
    isCover(newVal) {
      if (!newVal) {
        this.$nextTick(() => this._slideInLeft())
      }
    },
    '$route'() {
      this.$nextTick(() => {
        // 同時歸零 main 和內頁自身的捲動容器
        const targets = this.$el.querySelectorAll('main, main > div')
        targets.forEach(el => { el.scrollTop = 0 })
        // iOS Safari 慣性滾動結束後再補一次
        setTimeout(() => {
          targets.forEach(el => { el.scrollTop = 0 })
        }, 100)
      })
    },
  },

  methods: {
    toggleMenu() {
      this.$refs.mobileMenu.toggle()
      this.menuOpen = !this.menuOpen
    },

    _slideInLeft() {
      const left = this.$el.querySelector('.bookmark-nav--left')
      if (!left) return
      gsap.fromTo(left,
        { x: '-100%', opacity: 0 },
        { x: 0, opacity: 1, duration: 0.4, ease: 'power2.out', delay: 0.05 }
      )
    },

    async handleCoverNavigate(targetRoute) {
      if (this.$store.state.isFlipping) return
      this.$store.dispatch('setFlipping', true)

      // 執行封面前置動畫（圓環 snap-back、硬幣旋轉、發光）
      if (this.coverPreFlip) {
        await this.coverPreFlip()
      }

      const shell  = this.$refs.bookShell
      const body   = this.$refs.bookBody
      const flip   = this.$refs.bookFlip
      const shadow = this.$refs.flipShadow
      const wrap   = this.$refs.coverWrap
      const isMobile = window.innerWidth < 1025

      const flipDur  = isMobile ? 0.4 : 0.65
      const shiftDur = isMobile ? 0.3 : 0.45
      const alignDur = isMobile ? 0.2 : 0.25
      const shiftX = isMobile ? 0 : wrap.offsetWidth * 0.22
      const bodyLeft = body.getBoundingClientRect().left
      const finalShellX = -(bodyLeft)

      const tl = gsap.timeline({
        onComplete: () => {
          this.$store.dispatch('setFlipping', false)
          this.$router.push(targetRoute)
          this.$nextTick(() => {
            gsap.set([shell, flip, shadow], { clearProps: 'all' })
          })
        },
      })

      if (!isMobile) {
        tl.to(shell, { x: shiftX, duration: shiftDur, ease: 'power2.inOut' })
          .to(flip, { rotateY: -180, duration: flipDur, ease: 'power2.inOut' }, '-=0.05')
      } else {
        tl.to(flip, { rotateY: -180, duration: flipDur, ease: 'power2.inOut' })
      }

      tl.to(shadow, { opacity: 1, duration: flipDur * 0.35, ease: 'power1.in' }, isMobile ? '<' : `-=${flipDur}`)
        .to(shadow, { opacity: 0, duration: flipDur * 0.5, ease: 'power1.out' }, `<+=${flipDur * 0.4}`)
      tl.to(shell, { x: finalShellX, duration: alignDur, ease: 'power2.inOut' }, `>-${alignDur * 0.5}`)
    },

    async handleNavigate(targetRoute) {
      if (this.$store.state.isFlipping) return
      if (this.$route.path === targetRoute) return

      const fromIndex = this.currentIndex
      const toIndex   = BOOK_PAGES.findIndex(p => p.route === targetRoute)

      if (toIndex === 0) {
        return this._closeBook()
      }

      const direction = toIndex > fromIndex ? 'forward' : 'backward'
      const pageCount = Math.abs(toIndex - fromIndex)
      const isMobile  = window.innerWidth < 1025

      this.$store.dispatch('setFlipping', true)
      await this.$refs.pageFlip.play(direction, pageCount, isMobile)
      this.$store.dispatch('setFlipping', false)
      this.$router.push(targetRoute)
    },

    _closeBook() {
      if (this.$store.state.isFlipping) return
      this.$store.dispatch('setFlipping', true)

      const isMobile = window.innerWidth <= 768
      const overlay  = this.$refs.closeOverlay
      const closeDur = isMobile ? 0.35 : 0.5

      gsap.set(overlay, { display: 'block', x: '-100%' })

      gsap.timeline({
        onComplete: () => {
          this.$store.dispatch('setFlipping', false)
          this.$router.push('/')
          this.$nextTick(() => {
            gsap.set(overlay, { display: 'none', x: '-100%' })
          })
        },
      })
        .to(overlay, { x: '0%', duration: closeDur, ease: 'power2.inOut' })
    },
  },
}
</script>

<style lang="scss" scoped>
// perspective 相關（無 Tailwind utility）
.book-cover-wrap {
  perspective: 1400px;
  perspective-origin: left center;
}

// 左側補償塊的 max-width 計算式
.book-cover-group__balancer {
  max-width: calc(50% - (100vh - 2rem) * 0.375);
}

// 書脊漸層與偽元素
.book-cover__spine {
  width: 1.8rem;
  flex-shrink: 0;
  background: linear-gradient(to right, #0a0a0a 0%, #1c1c1c 60%, #242424 100%);
  border-right: 1px solid rgba(255, 255, 255, 0.08);

  &::before {
    content: '';
    position: absolute;
    left: 2px; top: 0; bottom: 0; width: 1px;
    background: linear-gradient(to bottom, transparent 5%, rgba(255,255,255,0.12) 30%, rgba(255,255,255,0.06) 70%, transparent 95%);
  }

  &::after {
    content: '';
    position: absolute;
    right: 0; top: 0; bottom: 0; width: 1px;
    background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.9) 20%, rgba(0,0,0,0.9) 80%, transparent);
  }
}

// 封面背面的偽元素
.book-flip__back::after {
  content: '';
  position: absolute;
  top: 0; bottom: 0; left: 0;
  width: 3rem;
  background: linear-gradient(to right, #0a0a0a 0%, #141414 60%, transparent 100%);
  pointer-events: none;
}

// 關書蓋板右緣光影
.book-close-overlay::after {
  content: '';
  position: absolute;
  top: 0; bottom: 0; right: 0;
  width: 8px;
  background: linear-gradient(to right, transparent 0%, rgba(255,255,255,0.12) 60%, rgba(255,255,255,0.25) 100%);
}

// 漢堡文字尺寸
.mobile-menu-btn__icon {
  font-size: 1.35rem;
  padding-left: 1.8rem;
  padding-right: 1.2rem;
}

// ≤1279px：書本滿版，無書脊書背，使用漢堡
@media (max-width: 1279px) {
  .book-inner-wrap {
    height: 100dvh !important;
    margin: 0 !important;
    filter: none !important;
  }

  .book-cover-wrap {
    padding: 0 !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .book-cover-group {
    height: 100dvh !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .book-shell {
    width: 100% !important;
    height: 100dvh !important;
    aspect-ratio: unset !important;
    max-height: 100dvh !important;
  }

  // 書背（BookmarkNav::before 偽元素）隱藏
  .bookmark-nav::before {
    display: none;
  }
}

// 手機漢堡燕尾形（與左側書籤相同造型，朝左）
.mobile-menu-btn {
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 1.1rem 50%);
  background: linear-gradient(135deg, #2e2e2e 0%, #1e1e1e 60%, #181818 100%);
  box-shadow: -2px 2px 8px rgba(0,0,0,0.6), -1px 1px 3px rgba(0,0,0,0.4);
  width: 3.8rem;
  transition: width 0.28s cubic-bezier(0.4, 0, 0.2, 1), right 0.28s cubic-bezier(0.4, 0, 0.2, 1), background 0.18s;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    clip-path: inherit;
    border-top:    1px solid rgba(255, 255, 255, 0.22);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    border-right:  1px solid rgba(255, 255, 255, 0.12);
    pointer-events: none;
  }

  &:hover {
    background: linear-gradient(135deg, #3a1215 0%, #2a1215 60%, #1e0e10 100%);
    &::before { border-top-color: rgba(195, 14, 35, 0.5); }
  }

  &:active {
    background: linear-gradient(135deg, #1e0e10 0%, #1a0b0d 100%);
    box-shadow: -1px 1px 4px rgba(0,0,0,0.6);
  }

  // 展開狀態：拉寬至抽屜等寬（w-48 = 12rem）
  &--open {
    width: 12rem;
    background: linear-gradient(135deg, #2a1215 0%, #1e1215 60%, #181010 100%);
    &::before { border-top-color: rgba(195, 14, 35, 0.35); }
  }

  // 搜尋抽屜展開時漢堡位移（數值可自行調整）
  &--search-open {
    width: 9rem;
  }

}

// blog/items 頁，漢堡展開時右移留出搜尋按鈕空間（:deep 繞過 scoped hash）
:deep(.mobile-menu-btn--has-search.mobile-menu-btn--open) {
  width: 9rem;
  background: linear-gradient(135deg, #2a1215 0%, #1e1215 60%, #181010 100%);
}

</style>
